import { useMutation } from "@apollo/client";
import { useCallback, useRef } from "react";

import { analyticsService } from "../../../lib/analytics";
import { sentryService } from "../../../lib/sentry";
import { acceptTermsMutation } from "./queries";

// Version identifier of the Terms/Privacy the user accepts (a FE constant);
// the server stamps the time.
export const TERMS_VERSION = "2026-06-01";

// Terms acceptance is tied to the checkbox press, not the form submit: the
// press fires the API call, and the submit path awaits the same single-flight
// promise. A failed attempt clears, so the next call (re-press or submit)
// retries; a success latches — re-affirming on every press would be noise.
export function useAcceptTerms() {
  const [acceptTerms] = useMutation(acceptTermsMutation);
  const acceptedRef = useRef(false);
  const pendingRef = useRef<Promise<boolean> | null>(null);

  const requestAcceptance = useCallback((): Promise<boolean> => {
    if (acceptedRef.current) {
      return Promise.resolve(true);
    }
    if (pendingRef.current) {
      return pendingRef.current;
    }
    const attempt = acceptTerms({
      variables: { input: { termsVersion: TERMS_VERSION } },
    })
      .then(() => {
        acceptedRef.current = true;
        analyticsService.trackEvent("TermsAccepted", {
          termsVersion: TERMS_VERSION,
        });
        return true;
      })
      .catch(error => {
        sentryService.captureException(error);
        return false;
      })
      .finally(() => {
        pendingRef.current = null;
      });
    pendingRef.current = attempt;
    return attempt;
  }, [acceptTerms]);

  return { requestAcceptance };
}
